import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";

type Theme = "dark" | "light" | "system";

interface ThemeContextProps {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

interface ThemeSyncMessage {
  type: "THEME_CHANGE";
  theme: Theme;
  timestamp: number;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

/* eslint-disable react-refresh/only-export-components */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
}

export function ThemeProvider({
  children,
  defaultTheme = "dark",
}: ThemeProviderProps) {
  // Track all active iframes for guaranteed cleanup
  const iframeRefs = useRef<Set<HTMLIFrameElement>>(new Set());

  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const stored = localStorage.getItem("theme");
      if (stored && ["dark", "light", "system"].includes(stored)) {
        return stored as Theme;
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("Failed to read theme from localStorage:", error);
      }
    }
    return defaultTheme;
  });

  const getEffectiveTheme = (currentTheme: Theme): "dark" | "light" => {
    if (currentTheme === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return currentTheme;
  };

  const updateDocumentTheme = (effectiveTheme: "dark" | "light") => {
    const root = document.documentElement;
    const body = document.body;

    if (effectiveTheme === "dark") {
      root.classList.add("dark");
      body.style.backgroundColor = "black";
    } else {
      root.classList.remove("dark");
      body.style.backgroundColor = "";
    }
  };

  const setTheme = (newTheme: Theme) => {
    try {
      localStorage.setItem("theme", newTheme);
      setThemeState(newTheme);

      const effectiveTheme = getEffectiveTheme(newTheme);
      updateDocumentTheme(effectiveTheme);

      broadcastThemeChange(newTheme);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn("Failed to save theme to localStorage:", error);
      }
      setThemeState(newTheme);

      const effectiveTheme = getEffectiveTheme(newTheme);
      updateDocumentTheme(effectiveTheme);
    }
  };

  const broadcastThemeChange = (newTheme: Theme) => {
    const message: ThemeSyncMessage = {
      type: "THEME_CHANGE",
      theme: newTheme,
      timestamp: Date.now(),
    };

    const domains = [
      "https://facetech.my",
      "https://www.facetech.my",
      "https://docs.facetech.my",
    ];

    domains.forEach((domain) => {
      try {
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = `${domain}/theme-sync.html`;

        // Track this iframe
        iframeRefs.current.add(iframe);

        // Single cleanup function to prevent race conditions
        let isCleanedUp = false;
        const cleanup = () => {
          if (isCleanedUp) return;
          isCleanedUp = true;

          if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
          iframeRefs.current.delete(iframe);
        };

        iframe.onload = () => {
          try {
            iframe.contentWindow?.postMessage(message, domain);
          } catch (error) {
            if (import.meta.env.DEV) {
              console.debug(
                `Cross-domain message failed for ${domain}:`,
                error,
              );
            }
          }

          // Cleanup after message sent (1s delay for processing)
          setTimeout(cleanup, 1000);
        };

        // Cleanup on error
        iframe.onerror = cleanup;

        document.body.appendChild(iframe);

        // Fallback cleanup if onload/onerror never fire (5s timeout)
        setTimeout(cleanup, 5000);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.debug(`Failed to create iframe for ${domain}:`, error);
        }
      }
    });
  };

  const handleCrossDomainMessage = useCallback((event: MessageEvent) => {
    const allowedOrigins = [
      "https://facetech.my",
      "https://www.facetech.my",
      "https://docs.facetech.my",
    ];

    if (!allowedOrigins.includes(event.origin)) {
      return;
    }

    try {
      const message = event.data as ThemeSyncMessage;

      if (
        message &&
        typeof message === "object" &&
        message.type === "THEME_CHANGE" &&
        typeof message.theme === "string" &&
        ["dark", "light", "system"].includes(message.theme) &&
        typeof message.timestamp === "number"
      ) {
        const currentTime = Date.now();
        if (Math.abs(currentTime - message.timestamp) < 10000) {
          localStorage.setItem("theme", message.theme);
          setThemeState(message.theme as Theme);

          const effectiveTheme = getEffectiveTheme(message.theme as Theme);
          updateDocumentTheme(effectiveTheme);
        }
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.debug("Error processing cross-domain theme message:", error);
      }
    }
  }, []);

  useEffect(() => {
    const effectiveTheme = getEffectiveTheme(theme);
    updateDocumentTheme(effectiveTheme);
  }, [theme]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleSystemThemeChange = () => {
      if (theme === "system") {
        const effectiveTheme = getEffectiveTheme("system");
        updateDocumentTheme(effectiveTheme);
      }
    };

    mediaQuery.addEventListener("change", handleSystemThemeChange);

    window.addEventListener("message", handleCrossDomainMessage);

    // Capture current iframes ref for cleanup
    const currentIframes = iframeRefs.current;

    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
      window.removeEventListener("message", handleCrossDomainMessage);

      // Cleanup all tracked iframes on unmount
      currentIframes.forEach((iframe) => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      });
      currentIframes.clear();
    };
  }, [theme, handleCrossDomainMessage]);

  // Memoize context value to prevent unnecessary re-renders of consumers
  // setTheme is stable (useCallback with no deps), so only re-create when theme changes
  const contextValue = useMemo(
    () => ({
      theme,
      setTheme,
    }),
    [theme], // setTheme is stable, no need to include
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}
